<script lang="ts" module>
  export interface SegmentedOption<T extends string = string> {
    value: T;
    label: string;
    /** Emoji or short glyph before the label (decorative). */
    icon?: string;
    /** Grey count after the label, e.g. Library 12. */
    count?: number;
    /** Optional status-colour accent; other switchers keep their existing look. */
    accent?: string;
    /** Renders the segment as a link (Inertia) instead of a button. */
    href?: string;
  }
</script>

<script lang="ts" generics="T extends string">
  /**
   * SegmentedControl - iOS-style segments on a grey track: 44px-tall, equal
   * width, the selected one an inset white pill. For view switchers and
   * filters on phones (Timeline views, View options, Cycles tabs, ...).
   *
   * Buttons report `aria-pressed`; segments with an `href` are links with
   * `aria-current="page"` and still call `onChange` on click.
   */
  import { Link } from '@inertiajs/svelte';

  interface Props {
    options: SegmentedOption<T>[];
    /** The selected value; null selects nothing. */
    value: T | null;
    /** Accessible name of the group. */
    label: string;
    onChange?: (value: T) => void;
  }

  let { options, value, label, onChange }: Props = $props();

  let links = $derived(options.some((option) => option.href));
</script>

{#snippet content(option: SegmentedOption)}
  {#if option.icon}<span aria-hidden="true">{option.icon}</span>{/if}
  {option.label}
  {#if option.count !== undefined}<span class="seg-count">{option.count}</span>{/if}
{/snippet}

{#if links}
  <nav class="seg" aria-label={label}>
    {#each options as option (option.value)}
      {#if option.href}
        <Link
          href={option.href}
          class={`seg-item${option.value === value ? ' on' : ''}`}
          style={option.accent ? `border-top: 3px solid ${option.accent}` : undefined}
          aria-current={option.value === value ? 'page' : undefined}
          onclick={() => onChange?.(option.value)}>{@render content(option)}</Link
        >
      {:else}
        <button
          type="button"
          class="seg-item"
          style:border-top={option.accent ? `3px solid ${option.accent}` : undefined}
          class:on={option.value === value}
          aria-pressed={option.value === value}
          onclick={() => onChange?.(option.value)}>{@render content(option)}</button
        >
      {/if}
    {/each}
  </nav>
{:else}
  <div class="seg" role="group" aria-label={label}>
    {#each options as option (option.value)}
      <button
        type="button"
        class="seg-item"
        style:border-top={option.accent ? `3px solid ${option.accent}` : undefined}
        class:on={option.value === value}
        aria-pressed={option.value === value}
        onclick={() => onChange?.(option.value)}>{@render content(option)}</button
      >
    {/each}
  </div>
{/if}

<style>
  .seg {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    border-radius: 12px;
    background: #f1f5f9;
  }

  .seg :global(.seg-item) {
    position: relative;
    isolation: isolate;
    display: flex;
    flex: 1 1 0;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-width: 0;
    height: 44px;
    padding: 0 4px;
    border: 0;
    background: none;
    color: #4b5563;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .seg :global(.seg-item)::before {
    content: '';
    position: absolute;
    inset: 4px 2px;
    z-index: -1;
    border-radius: 9px;
  }

  .seg :global(.seg-item.on) {
    color: #111827;
  }

  .seg :global(.seg-item.on)::before {
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  }

  .seg-count {
    color: #9ca3af;
    font-weight: 500;
  }
</style>
